// src/pages/EmployeeTraining.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { auth, db } from "../firebase/config";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import "../styles/EmployeeTraining.css";
import Oiiko from "../tools/oiikologo.png";
import { DateTime } from "luxon";
import { SquareLibrary } from "lucide-react";
import { useAuth } from "../Context/AuthContext";

// how strict you want to be
const VIDEO_COMPLETION_RATIO = 0.9; // 90% must be watched
const SEEK_TOLERANCE_SECONDS = 1.0; // small wiggle room
const MIN_PDF_SECONDS = 2; // must keep PDF open this long

const formatTime = (s = 0) => {
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

/**
 * Sort courses for employee view:
 * 1) Incomplete before completed
 * 2) Among incomplete:
 *    - Overdue first
 *    - Then upcoming by earliest deadline
 *    - Then no-deadline by newest assignedAt
 * 3) Completed at bottom (newest assignedAt first)
 */
const sortTrainingCourses = (courses, companyTimeZone) => {
  const now = DateTime.now()
    .setZone(companyTimeZone)
    .startOf("day");

  const getDeadlineDay = (c) => {
    if (!c.assignmentDeadline) return null;

    const jsDate = c.assignmentDeadline.toDate
      ? c.assignmentDeadline.toDate()
      : new Date(c.assignmentDeadline);

    if (Number.isNaN(jsDate.getTime())) return null;

    // compare by calendar day in company TZ
    return DateTime.fromJSDate(jsDate, { zone: companyTimeZone }).startOf(
      "day",
    );
  };

  const getAssignedAt = (c) => {
    const a = c.assignedAt;
    if (!a) return 0;
    if (a.toMillis) return a.toMillis();
    return new Date(a).getTime();
  };

  return [...courses].sort((a, b) => {
    const aCompleted = a.status === "completed";
    const bCompleted = b.status === "completed";

    // 1) Incomplete before completed
    if (!aCompleted && bCompleted) return -1;
    if (aCompleted && !bCompleted) return 1;

    const aDeadline = getDeadlineDay(a);
    const bDeadline = getDeadlineDay(b);

    const aOverdue = !aCompleted && aDeadline && aDeadline < now;
    const bOverdue = !bCompleted && bDeadline && bDeadline < now;

    // 2) Overdue first (among incomplete)
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    // 3) If both have deadlines, earlier deadline first
    if (aDeadline && bDeadline) {
      const diff = aDeadline.toMillis() - bDeadline.toMillis();
      if (diff !== 0) return diff;
    } else if (aDeadline && !bDeadline) {
      // Has deadline comes before no deadline
      return -1;
    } else if (!aDeadline && bDeadline) {
      return 1;
    }

    // 4) Fallback: newest assignedAt first
    const aAssigned = getAssignedAt(a);
    const bAssigned = getAssignedAt(b);
    return bAssigned - aAssigned;
  });
};

export default function EmployeeTraining() {
  const [companyId, setCompanyId] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [trainingCourses, setTrainingCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [activeCourse, setActiveCourse] = useState(null);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [viewState, setViewState] = useState({});
  const [activeTab, setActiveTab] = useState("dashboard"); // "dashboard" | "training"
  const { user, loading: authLoading } = useAuth();

  const pdfTimerRef = useRef(null);
  const videoRef = useRef(null);

  // ensure local tracking state exists for a course
  const ensureCourseState = (course) => {
    setViewState((prev) => {
      if (prev[course.id]) return prev;
      return {
        ...prev,
        [course.id]: {
          type: course.type,
          openedAt: null,
          pdfOpenSeconds: 0,
          video: {
            duration: 0,
            maxWatched: 0,
            lastTime: 0,
            completed: false,
            isPlaying: false,
          },
        },
      };
    });
  };

  const canCompleteCourse = (course) => {
    const s = viewState[course.id];
    if (!s) return false;

    if (course.type === "video") {
      const { duration, maxWatched, completed } = s.video || {};
      if (completed) return true;
      if (!duration || !maxWatched) return false;
      return maxWatched / duration >= VIDEO_COMPLETION_RATIO;
    }

    if (course.type === "pdf") {
      return (s.pdfOpenSeconds || 0) >= MIN_PDF_SECONDS;
    }

    // Links: consider "opened" when clicked
    if (course.type === "link") {
      return !!s.openedAt;
    }

    return false;
  };

  // ✅ Helper: get assignment deadline as Luxon DT (company TZ)
  const getAssignmentDeadlineDT = useCallback(
    (course) => {
      if (!course?.assignmentDeadline) return null;

      const jsDate = course.assignmentDeadline?.toDate
        ? course.assignmentDeadline.toDate()
        : new Date(course.assignmentDeadline);

      if (Number.isNaN(jsDate.getTime())) return null;

      return DateTime.fromJSDate(jsDate, { zone: companyTimeZone });
    },
    [companyTimeZone],
  );

  // ✅ Helper: day-diff by CALENDAR DAY (fixes "Due today" bug)
  const daysDiffCalendar = useCallback(
    (dueDT) => {
      if (!dueDT) return null;
      const today = DateTime.now()
        .setZone(companyTimeZone)
        .startOf("day");
      const dueDay = dueDT.setZone(companyTimeZone).startOf("day");
      return Math.trunc(dueDay.diff(today, "days").days); // integer
    },
    [companyTimeZone],
  );

  // ✅ Helper: overdue by calendar day (not time-of-day)
  const isOverdueCalendar = useCallback(
    (course) => {
      if (course?.status === "completed") return false;
      const dueDT = getAssignmentDeadlineDT(course);
      if (!dueDT) return false;
      return daysDiffCalendar(dueDT) < 0;
    },
    [getAssignmentDeadlineDT, daysDiffCalendar],
  );

  // Find company/employee and timezone
  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");

        if (authLoading) return;

        if (!user?.uid) {
          setError("Employee not authenticated.");
          setCompanyId(null);
          setEmployeeId(null);
          return;
        }

        const uid = user.uid;
        setEmployeeId(uid);

        // ✅ get companyId without scanning
        let cid = user.companyId || null;

        if (!cid) {
          const userSnap = await getDoc(doc(db, "users", uid));
          cid = userSnap.exists() ? userSnap.data()?.companyId || null : null;
        }

        if (!cid) throw new Error("Company ID not found.");
        setCompanyId(cid);

        // ✅ load TZ from company doc (single read)
        const companySnap = await getDoc(doc(db, "companies", cid));
        const tz = companySnap.exists()
          ? companySnap.data()?.timeZone || "UTC"
          : "UTC";
        setCompanyTimeZone(tz);
      } catch (err) {
        setError(err.message || "Failed to load company info.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [user?.uid, user?.companyId, authLoading]);

  // Fetch assigned training for this employee
  useEffect(() => {
    if (!companyId || !employeeId) return;

    const fetchTrainingCourses = async () => {
      try {
        setLoading(true);

        const progressRef = collection(
          db,
          `companies/${companyId}/employees/${employeeId}/training_progress`,
        );
        const progressSnapshot = await getDocs(progressRef);

        // Each doc is keyed by courseId (in your assign flow), so id == courseId
        const assignedCourses = progressSnapshot.docs.map((docSnap) => ({
          id: docSnap.id, // courseId
          ...docSnap.data(),
        }));

        // ✅ join with course metadata WITHOUT overwriting assignment deadline
        const coursesData = await Promise.all(
          assignedCourses.map(async (progressRow) => {
            const courseRef = doc(
              db,
              `companies/${companyId}/training_courses`,
              progressRow.courseId,
            );
            const courseDoc = await getDoc(courseRef);

            if (!courseDoc.exists()) return null;

            const courseData = courseDoc.data() || {};

            return {
              ...courseData, // title/description/type/contentUrl/thumbnailUrl etc
              ...progressRow, // status/assignedAt/deadline
              assignmentDeadline: progressRow.deadline || null, // ✅ source of truth
            };
          }),
        );

        const fullCourses = coursesData.filter(Boolean);
        setTrainingCourses(fullCourses);

        // Overdue notification (based on assignment deadline DAY)
        const now = DateTime.now().setZone(companyTimeZone);

        for (const overdue of fullCourses.filter((c) => isOverdueCalendar(c))) {
          const dueDT = getAssignmentDeadlineDT(overdue);
          const dueKey = dueDT ? dueDT.toISODate() : "no-deadline";

          // ✅ stable per (employee, course, due date)
          const notificationId = `${employeeId}_${overdue.id}_overdue_${dueKey}`;

          const notificationRef = doc(
            db,
            `companies/${companyId}/notifications`,
            notificationId,
          );
          const notificationDoc = await getDoc(notificationRef);

          if (!notificationDoc.exists()) {
            await setDoc(notificationRef, {
              employeeId,
              courseId: overdue.id,
              courseTitle: overdue.courseTitle,
              employeeName: auth.currentUser?.displayName || "Employee",
              type: "trainingMissed",
              status: "overdue",
              timestamp: now.toJSDate(),
              read: false,
              message: `Training "${overdue.courseTitle}" is overdue for ${auth
                .currentUser?.displayName || "Employee"}.`,
            });
          }
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch training courses.");
      } finally {
        setLoading(false);
      }
    };

    fetchTrainingCourses();
  }, [
    companyId,
    employeeId,
    companyTimeZone,
    getAssignmentDeadlineDT,
    isOverdueCalendar,
  ]);

  const handleCompleteTraining = async (courseId) => {
    try {
      const progressRef = doc(
        db,
        `companies/${companyId}/employees/${employeeId}/training_progress`,
        courseId,
      );
      await updateDoc(progressRef, { status: "completed" });

      const empSnap = await getDoc(
        doc(db, `companies/${companyId}/employees`, employeeId),
      );
      const employeeName = empSnap.exists()
        ? empSnap.data().name || "Unknown"
        : "Unknown";

      const courseTitle =
        trainingCourses.find((c) => c.id === courseId)?.courseTitle ||
        "Training Course";

      await setDoc(
        doc(
          db,
          `companies/${companyId}/notifications`,
          `${employeeId}_${courseId}_completed`,
        ),
        {
          employeeId,
          courseId,
          employeeName,
          type: "trainingCompleted",
          status: "completed",
          timestamp: new Date(),
          read: false,
          message: `Training "${courseTitle}" completed by ${employeeName}.`,
        },
      );

      setTrainingCourses((prev) =>
        prev.map((course) =>
          course.id === courseId ? { ...course, status: "completed" } : course,
        ),
      );
    } catch (err) {
      alert("Failed to mark training as completed.");
    }
  };

  // Pre-compute sorted list for rendering
  const sortedCourses = sortTrainingCourses(trainingCourses, companyTimeZone);

  // Dashboard metrics
  const totalAssignments = trainingCourses.length;
  const completedAssignments = trainingCourses.filter(
    (c) => c.status === "completed",
  ).length;
  const completionPercent =
    totalAssignments > 0
      ? Math.round((completedAssignments / totalAssignments) * 100)
      : 0;

  // ✅ Due label uses calendar-day diff (fixes "Due today" when it's actually tomorrow)
  const getDueLabel = (course) => {
    const dueDT = getAssignmentDeadlineDT(course);
    if (!dueDT) return "No due date";

    const d = daysDiffCalendar(dueDT);

    if (d > 0) return `${d} day${d === 1 ? "" : "s"} left until due date`;
    if (d === 0) return "Due today";

    const past = Math.abs(d);
    return `${past} day${past === 1 ? "" : "s"} past due`;
  };

  const dashboardHasAssignments = totalAssignments > 0;

  return (
    <div className="employeetraining-container">
      {/* Top bar with tabs */}
      <div className="employeetraining-nav">
        <div className="employeetraining-nav-left">
          <img src={Oiiko} alt="Oiiko Logo" className="company-logo" />
          <h1>Oiiko Training</h1>
        </div>
        <div className="employeetraining-nav-tabs">
          <button
            type="button"
            className={`training-tab ${
              activeTab === "dashboard" ? "active" : ""
            }`}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard
          </button>
          <button
            type="button"
            className={`training-tab ${
              activeTab === "training" ? "active" : ""
            }`}
            onClick={() => setActiveTab("training")}
          >
            Training
          </button>
        </div>
      </div>

      <div className="employeetraining">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="employeetraining-error">{error}</p>
        ) : activeTab === "dashboard" ? (
          /* ========================
             DASHBOARD TAB
             ======================== */
          <div className="training-dashboard">
            {/* Progress Card */}
            <div className="training-progress-card">
              <div className="training-progress-header">
                <div className="training-progress-title-block">
                  <div className="training-progress-icon">
                    <span>🎓</span>
                  </div>
                  <div>
                    <div className="training-progress-title">
                      Training Progress
                    </div>
                    <div className="training-progress-subtitle">Completed</div>
                  </div>
                </div>
                <div className="training-progress-percent">
                  {completionPercent}%
                </div>
              </div>

              <div className="training-progress-bar-outer">
                <div
                  className="training-progress-bar-inner"
                  style={{ width: `${completionPercent}%` }}
                />
              </div>

              <div className="training-progress-footer">
                <div className="training-progress-footer-left">
                  {dashboardHasAssignments ? (
                    <>
                      You have completed{" "}
                      <strong>
                        {completedAssignments} of {totalAssignments}
                      </strong>{" "}
                      assignments
                    </>
                  ) : (
                    <>No training has been assigned yet.</>
                  )}
                </div>
                <button
                  type="button"
                  className="training-progress-footer-btn"
                  onClick={() => setActiveTab("training")}
                  disabled={!dashboardHasAssignments}
                >
                  Go to Training
                </button>
              </div>
            </div>

            {/* Assignments Table */}
            <div className="training-assignments-card">
              <table className="training-assignments-table">
                <thead>
                  <tr>
                    <th>Assignment name</th>
                    <th>Status</th>
                    <th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCourses.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="training-assignments-empty">
                        No assignments available.
                      </td>
                    </tr>
                  ) : (
                    sortedCourses.map((course) => (
                      <tr key={course.id}>
                        <td>{course.courseTitle}</td>
                        <td>
                          {course.status === "completed"
                            ? "Completed"
                            : course.status === "started"
                            ? "Started"
                            : "Not started"}
                        </td>
                        <td>{getDueLabel(course)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* ========================
             TRAINING TAB (existing UI)
             ======================== */
          <>
            <div className="employeetraining-banner-wrapper">
              <div className="employeetraining-complete-banner">
                {trainingCourses.length === 0 ? (
                  <>
                    <h2>No training assigned</h2>
                    <p>We'll notify you once your training is available.</p>
                  </>
                ) : trainingCourses.every((c) => c.status === "completed") ? (
                  <>
                    <h2>You have completed all of your required training!</h2>
                  </>
                ) : (
                  <>
                    <h2>Training is in progress</h2>
                    <p>Please complete your assigned courses below.</p>
                  </>
                )}
              </div>
            </div>

            <div className="employeetraining-scrollable">
              {sortedCourses.length > 0 && (
                <div className="employeetraining-grid">
                  {sortedCourses.map((course) => {
                    const dueDT = getAssignmentDeadlineDT(course);
                    const overdue = isOverdueCalendar(course);

                    return (
                      <div key={course.id} className="employeetraining-card">
                        <div className="training-thumb">
                          <SquareLibrary
                            className="training-thumb-icon"
                            size={64}
                          />
                        </div>

                        <div className="employeetraining-card-body">
                          <span
                            className={`employeetraining-status-badge ${
                              course.status === "completed"
                                ? "completed"
                                : overdue
                                ? "overdue"
                                : "in-progress"
                            }`}
                          >
                            {course.status === "completed"
                              ? "Completed"
                              : overdue
                              ? "Overdue"
                              : "⏳ In Progress"}
                          </span>

                          <p className="employeetraining-deadline">
                            Deadline:{" "}
                            {dueDT
                              ? dueDT.toFormat("ccc, dd LLL yyyy")
                              : "No deadline"}
                          </p>

                          <h3>{course.courseTitle}</h3>
                          <p>{course.description}</p>

                          <button
                            type="button"
                            className="employeetraining-link"
                            onClick={() => {
                              ensureCourseState(course);
                              setActiveCourse(course);
                              setModalOpen(true);
                            }}
                          >
                            Start
                          </button>

                          <div className="employeetraining-status-row">
                            {course.status !== "completed" && (
                              <button
                                onClick={() =>
                                  handleCompleteTraining(course.id)
                                }
                                className="employeetraining-complete-btn"
                                disabled={!canCompleteCourse(course)}
                                title={
                                  canCompleteCourse(course)
                                    ? "Mark as Completed"
                                    : course.type === "video"
                                    ? "Watch the video to (at least) 90% without skipping"
                                    : course.type === "pdf"
                                    ? `Keep the PDF open for at least ${MIN_PDF_SECONDS}s`
                                    : "Open the training content"
                                }
                                style={{
                                  opacity: canCompleteCourse(course) ? 1 : 0.6,
                                  cursor: canCompleteCourse(course)
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                              >
                                Mark as Completed
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Modal */}
              {modalOpen && activeCourse && (
                <div
                  className="employeetraining-modal-overlay"
                  onClick={() => {
                    if (pdfTimerRef.current) {
                      clearInterval(pdfTimerRef.current);
                      pdfTimerRef.current = null;
                    }
                    setModalOpen(false);
                  }}
                >
                  <div
                    className="employeetraining-modal-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="employeetraining-modal-close"
                      onClick={() => {
                        if (pdfTimerRef.current) {
                          clearInterval(pdfTimerRef.current);
                          pdfTimerRef.current = null;
                        }
                        setModalOpen(false);
                      }}
                    >
                      ✖
                    </button>

                    <h2>{activeCourse.courseTitle}</h2>
                    <p>{activeCourse.description}</p>

                    {/* VIDEO */}
                    {activeCourse.type === "video" && (
                      <div
                        className="employeetraining-video-wrapper"
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        <video
                          ref={videoRef}
                          width="100%"
                          controls={false}
                          onKeyDown={(e) => {
                            const keys = [
                              "ArrowLeft",
                              "ArrowRight",
                              "Home",
                              "End",
                              "PageUp",
                              "PageDown",
                            ];
                            if (keys.includes(e.key)) e.preventDefault();
                          }}
                          tabIndex={0}
                          onLoadedMetadata={(e) => {
                            const duration = e.currentTarget.duration || 0;

                            setViewState((prev) => {
                              const prevState = prev[activeCourse.id] || {};
                              const prevVideo = prevState.video || {};

                              return {
                                ...prev,
                                [activeCourse.id]: {
                                  ...prevState,
                                  type: "video",
                                  video: {
                                    ...prevVideo,
                                    duration:
                                      duration || prevVideo.duration || 0,
                                    maxWatched: prevVideo.maxWatched || 0,
                                    lastTime: prevVideo.lastTime || 0,
                                    completed: !!prevVideo.completed,
                                    isPlaying: false,
                                  },
                                },
                              };
                            });
                          }}
                          onTimeUpdate={(e) => {
                            const t = e.currentTarget.currentTime || 0;
                            setViewState((prev) => {
                              const cur = prev[activeCourse.id]?.video || {};
                              const newMax = Math.max(cur.maxWatched || 0, t);
                              return {
                                ...prev,
                                [activeCourse.id]: {
                                  ...(prev[activeCourse.id] || {}),
                                  video: {
                                    ...cur,
                                    maxWatched: newMax,
                                    lastTime: t,
                                  },
                                },
                              };
                            });
                          }}
                          onSeeking={(e) => {
                            const el = e.currentTarget;
                            setViewState((prev) => {
                              const cur = prev[activeCourse.id]?.video || {};
                              const allowed =
                                (cur.maxWatched || 0) + SEEK_TOLERANCE_SECONDS;
                              if (el.currentTime > allowed) {
                                el.currentTime = Math.min(
                                  allowed,
                                  cur.lastTime || 0,
                                );
                              }
                              return prev;
                            });
                          }}
                          onEnded={() => {
                            setViewState((prev) => {
                              const prevState = prev[activeCourse.id] || {};
                              const prevVideo = prevState.video || {};
                              const duration =
                                prevVideo.duration || prevVideo.maxWatched || 0;

                              return {
                                ...prev,
                                [activeCourse.id]: {
                                  ...prevState,
                                  video: {
                                    ...prevVideo,
                                    completed: true,
                                    isPlaying: false,
                                    maxWatched: duration,
                                  },
                                },
                              };
                            });
                          }}
                          onClick={() => {
                            const v = videoRef.current;
                            if (!v) return;
                            setViewState((prev) => {
                              const cur = prev[activeCourse.id]?.video || {};
                              const playing = !cur.isPlaying;
                              if (playing) v.play();
                              else v.pause();
                              return {
                                ...prev,
                                [activeCourse.id]: {
                                  ...(prev[activeCourse.id] || {}),
                                  video: { ...cur, isPlaying: playing },
                                },
                              };
                            });
                          }}
                          style={{ backgroundColor: "#000", borderRadius: 8 }}
                        >
                          <source
                            src={activeCourse.contentUrl}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>

                        {(() => {
                          const s = viewState[activeCourse.id]?.video || {};
                          const ratio = s.duration
                            ? Math.min(1, (s.lastTime || 0) / s.duration)
                            : 0;
                          const watchedRatio = s.duration
                            ? Math.min(1, (s.maxWatched || 0) / s.duration)
                            : 0;

                          return (
                            <>
                              <div className="employeetraining-video-controls">
                                <button
                                  type="button"
                                  className="video-btn"
                                  onClick={() => {
                                    const v = videoRef.current;
                                    if (!v) return;
                                    setViewState((prev) => {
                                      const cur =
                                        prev[activeCourse.id]?.video || {};
                                      const playing = !cur.isPlaying;
                                      if (playing) v.play();
                                      else v.pause();
                                      return {
                                        ...prev,
                                        [activeCourse.id]: {
                                          ...(prev[activeCourse.id] || {}),
                                          video: { ...cur, isPlaying: playing },
                                        },
                                      };
                                    });
                                  }}
                                >
                                  {s.isPlaying ? "Pause" : "Play"}
                                </button>

                                <div className="video-timer">
                                  {formatTime(s.lastTime || 0)} /{" "}
                                  {formatTime(s.duration || 0)}
                                </div>

                                <div
                                  className="video-progress-rail"
                                  aria-label="Video progress"
                                >
                                  <div
                                    className="video-progress-watched"
                                    style={{ width: `${watchedRatio * 100}%` }}
                                  />
                                  <div
                                    className="video-progress-head"
                                    style={{ left: `${ratio * 100}%` }}
                                  />
                                </div>

                                <button
                                  type="button"
                                  className="video-btn"
                                  onClick={() => {
                                    const v = videoRef.current;
                                    if (!v) return;
                                    v.currentTime = 0;
                                  }}
                                >
                                  Restart
                                </button>
                              </div>

                              {s.completed && (
                                <div className="training-finished-banner">
                                  <span>Done watching this training.</span>
                                  <button
                                    type="button"
                                    className="training-finished-btn"
                                    onClick={() => {
                                      if (pdfTimerRef.current) {
                                        clearInterval(pdfTimerRef.current);
                                        pdfTimerRef.current = null;
                                      }
                                      setModalOpen(false);
                                      setActiveCourse(null);
                                    }}
                                  >
                                    Done – Back to training
                                  </button>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* PDF */}
                    {activeCourse.type === "pdf" && (
                      <div
                        style={{
                          border: "1px solid #eee",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        <iframe
                          src={activeCourse.contentUrl}
                          width="100%"
                          height="500"
                          style={{ border: "none" }}
                          title="PDF Viewer"
                          onLoad={() => {
                            setViewState((prev) => ({
                              ...prev,
                              [activeCourse.id]: {
                                ...(prev[activeCourse.id] || {}),
                                type: "pdf",
                                openedAt: Date.now(),
                              },
                            }));
                            if (pdfTimerRef.current)
                              clearInterval(pdfTimerRef.current);
                            pdfTimerRef.current = setInterval(() => {
                              setViewState((prev) => {
                                const s = prev[activeCourse.id] || {};
                                const secs = (s.pdfOpenSeconds || 0) + 1;
                                return {
                                  ...prev,
                                  [activeCourse.id]: {
                                    ...s,
                                    pdfOpenSeconds: secs,
                                  },
                                };
                              });
                            }, 1000);
                          }}
                        />
                      </div>
                    )}

                    {/* LINK */}
                    {activeCourse.type === "link" && (
                      <a
                        href={activeCourse.contentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          setViewState((prev) => ({
                            ...prev,
                            [activeCourse.id]: {
                              ...(prev[activeCourse.id] || {}),
                              type: "link",
                              openedAt: Date.now(),
                            },
                          }))
                        }
                      >
                        Open External Link
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
